import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import { Search, BookOpen, ArrowRight, X } from "lucide-react";

export default function Glossary() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/glossary");
        setData(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    let list = data.entries;
    if (activeCategory !== "all") {
      list = list.filter((e) => e.category === activeCategory);
    }
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(
        (e) =>
          e.term.toLowerCase().includes(q) ||
          e.definition.toLowerCase().includes(q) ||
          e.category.toLowerCase().includes(q),
      );
    }
    return list;
  }, [data, query, activeCategory]);

  // Group filtered entries by category for display
  const grouped = useMemo(() => {
    const out = {};
    for (const e of filtered) {
      if (!out[e.category]) out[e.category] = [];
      out[e.category].push(e);
    }
    return out;
  }, [filtered]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-5xl mx-auto px-6 py-20 text-zinc-500">Loading code-reference index…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-12" data-testid="glossary-page">
        <div className="overline mb-2"><span className="red-bar" />Code reference</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter mb-3" data-testid="glossary-title">
          Glossary &amp; index
        </h1>
        <p className="text-zinc-600 max-w-2xl mb-8">
          Every code citation, UL standard, NFPA chapter, NYC reference, and acronym used across the
          70-hour curriculum and Level 2 bolt-on. Search a term — jump straight to the lesson that
          defines it. {data?.count} entries.
        </p>

        {/* Search */}
        <div className="relative max-w-md mb-5">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search NFPA 72, UL 864, MEA, STI-PA…"
            className="w-full border-2 border-zinc-200 focus:border-red-600 outline-none pl-9 pr-9 py-2.5 text-sm font-mono bg-white"
            data-testid="glossary-search"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-zinc-400 hover:text-red-600"
              data-testid="glossary-clear"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Category pills */}
        <div className="flex flex-wrap gap-1 mb-8">
          <button
            onClick={() => setActiveCategory("all")}
            className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider transition-colors border-2 ${
              activeCategory === "all"
                ? "bg-red-600 text-white border-red-600"
                : "border-zinc-200 text-zinc-700 hover:border-red-600 hover:text-red-600"
            }`}
            data-testid="glossary-cat-all"
          >
            All ({data?.count})
          </button>
          {data?.categories?.map((c) => {
            const count = data.entries.filter((e) => e.category === c).length;
            return (
              <button
                key={c}
                onClick={() => setActiveCategory(c)}
                className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider transition-colors border-2 ${
                  activeCategory === c
                    ? "bg-red-600 text-white border-red-600"
                    : "border-zinc-200 text-zinc-700 hover:border-red-600 hover:text-red-600"
                }`}
                data-testid={`glossary-cat-${c.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {c} ({count})
              </button>
            );
          })}
        </div>

        {/* Results */}
        {filtered.length === 0 ? (
          <div className="card-tech p-12 text-center text-zinc-500" data-testid="glossary-empty">
            <BookOpen className="w-10 h-10 mx-auto mb-3 text-zinc-400" />
            <p className="font-heading font-bold mb-1">No matches</p>
            <p className="text-sm">Try a different term or clear the filter.</p>
          </div>
        ) : (
          <div className="space-y-10" data-testid="glossary-results">
            {Object.entries(grouped).map(([cat, entries]) => (
              <section key={cat}>
                <div className="border-b-2 border-red-600 pb-2 mb-4">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-red-600">
                    {cat} · {entries.length}
                  </div>
                </div>
                <div className="space-y-4">
                  {entries.map((e) => (
                    <div
                      key={e.term}
                      className="border-l-4 border-red-600 pl-5 py-1"
                      data-testid={`glossary-entry-${e.term.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`}
                    >
                      <div className="flex items-start justify-between flex-wrap gap-3 mb-1">
                        <h3 className="font-heading text-xl font-bold tracking-tight">
                          {e.term}
                        </h3>
                        {e.lesson_ids?.length > 0 && (
                          <Link
                            to={`/lesson/${e.lesson_ids[0]}`}
                            className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-zinc-700 hover:text-red-600"
                            data-testid={`glossary-jump-${e.term.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`}
                          >
                            <BookOpen className="w-3 h-3" />
                            {e.lesson_ids[0].toUpperCase()}
                            <ArrowRight className="w-3 h-3" />
                          </Link>
                        )}
                      </div>
                      <p className="text-sm text-zinc-700 leading-relaxed">{e.definition}</p>
                      {e.lesson_ids && e.lesson_ids.length > 1 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {e.lesson_ids.slice(1).map((lid) => (
                            <Link
                              key={lid}
                              to={`/lesson/${lid}`}
                              className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 hover:text-red-600 border border-zinc-200 hover:border-red-600 px-2 py-0.5"
                            >
                              {lid}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
